import { type FormEvent, useEffect, useState } from "react";
import { Button, Input, Modal } from "@legal-500/legal500-design-system";
import {
  duplicateFieldOf,
  type IndustrySector,
  type IndustrySectorField,
  type UpdateIndustrySectorInput,
} from "../../api/industrySectors.service";
import styles from "./IndustrySectors.module.css";

export type IndustrySectorFormValues = {
  name: string;
  slug?: string;
};

type IndustrySectorFormModalProps = {
  isOpen: boolean;
  sector: IndustrySector | null;
  onClose: () => void;
  onSubmit: (values: IndustrySectorFormValues) => Promise<void>;
};

type FieldErrors = Partial<Record<IndustrySectorField, string>>;

const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

function renderedFields(isEdit: boolean): IndustrySectorField[] {
  return isEdit ? ["name", "slug"] : ["name"];
}

function validate(name: string, slug: string, isEdit: boolean): FieldErrors {
  const errors: FieldErrors = {};
  if (!name.trim()) errors.name = "Name is required";
  if (isEdit && !SLUG_PATTERN.test(slug)) {
    errors.slug = "Slug must be lowercase letters, digits and hyphens";
  }
  return errors;
}

export function changedFields(
  sector: IndustrySector,
  values: IndustrySectorFormValues,
): UpdateIndustrySectorInput {
  const changes: UpdateIndustrySectorInput = {};
  if (values.name !== sector.name) changes.name = values.name;
  if (values.slug !== undefined && values.slug !== sector.slug) changes.slug = values.slug;
  return changes;
}

export function IndustrySectorFormModal({
  isOpen,
  sector,
  onClose,
  onSubmit,
}: IndustrySectorFormModalProps) {
  const isEdit = sector !== null;
  const fields = renderedFields(isEdit);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setName(sector?.name ?? "");
    setSlug(sector?.slug ?? "");
    setFieldErrors({});
    setFormError(null);
    setSubmitting(false);
  }, [isOpen, sector]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const errors = validate(name, slug, isEdit);
    setFieldErrors(errors);
    setFormError(null);
    if (Object.keys(errors).length > 0) return;

    setSubmitting(true);
    try {
      await onSubmit({
        name: name.trim(),
        slug: isEdit ? slug : undefined,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      const field = duplicateFieldOf(error);
      if (field && fields.includes(field)) {
        setFieldErrors({ [field]: message });
      } else {
        setFormError(message);
      }
      setSubmitting(false);
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? "Edit industry sector" : "New industry sector"}
      size="small"
    >
      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        {formError && (
          <div className={styles.formError} role="alert">
            {formError}
          </div>
        )}
        <Input
          id="industry-sector-name"
          label="Name"
          value={name}
          onChange={event => setName(event.target.value)}
          error={fieldErrors.name}
          required
          fullWidth
          autoFocus
        />
        {fields.includes("slug") && (
          <Input
            id="industry-sector-slug"
            label="Slug"
            value={slug}
            onChange={event => setSlug(event.target.value)}
            error={fieldErrors.slug}
            required
            fullWidth
          />
        )}
        <div className={styles.formActions}>
          <Button variant="outlined" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="primary" disabled={submitting}>
            {submitting ? "Saving..." : isEdit ? "Save" : "Create"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
